import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  input,
  OnDestroy,
  OnInit,
  output,
  viewChild
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { Store } from "@viewer/core";
import { SharedModule } from "@viewer/shared-module/shared.module";
import { CommentService } from "@viewer/toc-items/comments-module/services/comment.service";
import { AttachedMedia, Comment } from "@orion2/models/tocitem.models";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatCardModule } from "@angular/material/card";
import { MatIconModule } from "@angular/material/icon";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  Validators,
  ReactiveFormsModule,
  FormControl,
  FormArray,
  ValidatorFn,
  AbstractControl,
  ValidationErrors
} from "@angular/forms";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { isEqual } from "lodash";
import { MaterialFileInputModule } from "ngx-custom-material-file-input";
import { NgTemplateOutlet, KeyValuePipe } from "@angular/common";
import { MessageService } from "libs/message-service/message.service";
import { MatSelectModule } from "@angular/material/select";
import { ALLOWED_EXTENSIONS_MIME_TYPES } from "@orion2/utils/constants.utils";

interface CommentCodeError {
  key?: string;
  field?: string;
  min?: number;
  max?: number;
  nbrChar?: number;
  typeChar?: string;
}

@Component({
  standalone: true,
  selector: "o-comment-edit",
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatCardModule,
    SharedModule,
    MatButtonModule,
    MatCheckboxModule,
    MatSelectModule,
    KeyValuePipe,
    MaterialFileInputModule,
    NgTemplateOutlet
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: "./comment-edit.component.html",
  styleUrl: "./comment-edit.component.scss"
})
export class CommentEditComponent implements OnInit, OnDestroy {
  public comment = input<Comment>();
  public endEdit = output();
  private readonly titleInput = viewChild.required<ElementRef<HTMLElement>>("titleInput");
  public form: FormGroup;
  public titleMaxLenght = 200;
  public readonly priorityOptions = ["cp01", "cp02", "cp03"];
  public noSort = () => 0;
  public firstCodeError: CommentCodeError;
  public readonly ALLOWED_MIME_TYPES_LIST = Array.from(ALLOWED_EXTENSIONS_MIME_TYPES.values()).join(
    ", "
  );

  constructor(
    private readonly formBuilder: FormBuilder,
    public readonly commentService: CommentService,
    private readonly messageService: MessageService,
    public readonly store: Store
  ) {}

  get formParagraphs(): FormArray<FormControl<string>> {
    return this.form.get("paragraphs") as FormArray<FormControl<string>>;
  }

  get formMedias(): FormArray<FormControl<AttachedMedia>> {
    return this.form.get("medias") as FormArray<FormControl<AttachedMedia>>;
  }

  ngOnInit(): void {
    if (!this.comment().title || this.comment().fromNote) {
      this.commentService.addCommentEditComponent(this);
    }
    setTimeout(() => {
      this.titleInput().nativeElement?.focus();
    });
    this.form = this.formBuilder.group({
      title: this.formBuilder.control(this.comment().title, [
        Validators.required,
        Validators.maxLength(this.titleMaxLenght),
        Validators.pattern(/\.*\S+.*/)
      ]),
      priority: this.formBuilder.control(this.comment().priority, [Validators.required]),
      code: this.formBuilder.group(
        {
          model: this.formBuilder.control(this.comment().model, [
            Validators.required,
            Validators.pattern(/^\w{2,14}$/)
          ]),
          cageCode: this.formBuilder.control(this.comment().cageCode, [
            Validators.required,
            Validators.pattern(/^\w{5}$/)
          ]),
          year: this.formBuilder.control(this.comment().year, [
            Validators.required,
            Validators.pattern(/^\d{4}$/)
          ]),
          seqNumber: this.formBuilder.control(this.comment().seqNumber, [
            Validators.required,
            Validators.pattern(/^\d{5}$/)
          ]),
          commentType: this.formBuilder.control(
            { value: this.comment().commentType?.toUpperCase(), disabled: true },
            [Validators.required]
          )
        },
        {
          validators: [this.checkDuplicate]
        }
      )
    });

    const paragraphValidators = [Validators.required, Validators.pattern(/\.*\S+.*/)];
    const paragraphControlArray = this.formBuilder.array([], [Validators.required]);
    const mediaControlArray = this.formBuilder.array([], []);
    this.comment().contents.forEach((paragraph: string, index: number) => {
      // SPEC: The first paragraph should be complete but the other aren't required.
      const control = this.formBuilder.control(paragraph, index === 0 ? paragraphValidators : []);
      paragraphControlArray.push(control, { emitEvent: false });
    });
    (this.comment().medias || []).forEach((media: AttachedMedia) => {
      // SPEC: The first paragraph should be complete but the other aren't required.
      const control = this.formBuilder.control(media, []);
      mediaControlArray.push(control, { emitEvent: false });
    });
    this.form.addControl("paragraphs", paragraphControlArray, { emitEvent: false });
    this.form.addControl("medias", mediaControlArray, { emitEvent: false });
    this.formCodeChanged();
  }

  ngOnDestroy(): void {
    this.commentService.removeCommentEditComponent(this);
  }

  /**
   * Saves the edition of the comment.
   * @public
   * @param {Event} [event] event from DOM
   * @returns {Promise<boolean>} `true` if the save was successful, `false` otherwise.
   */
  public save(event?: Event): Promise<boolean> {
    // if event is present, it's by triggering dom event (click on save button).
    // We need to stop propagation to not trigger redirect in case of DM's comment
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!this.form.valid) {
      return Promise.resolve(false);
    }

    const formRawValue = this.form.getRawValue();
    const code = this.commentService.constructCommentCode(formRawValue.code);

    const newComment = {
      ...this.comment(),
      // Overwrite the author when saving a comment.
      author: this.store.user.userName,
      code,
      title: formRawValue.title,
      priority: formRawValue.priority,
      model: formRawValue.code.model.toUpperCase(),
      cageCode: formRawValue.code.cageCode.toUpperCase(),
      year: formRawValue.code.year,
      seqNumber: formRawValue.code.seqNumber,
      commentType: formRawValue.code.commentType.toLowerCase(),
      // SPEC: When we save the comment the empty paragraphes aren't saved.
      contents: formRawValue.paragraphs.filter((paragraph: string) => /\.*\S+.*/.test(paragraph)),
      medias: formRawValue.medias
    };
    const fromNote = newComment.fromNote || false;
    delete newComment.fromNote;

    this.endEdit.emit();
    return this.commentService.updateComment(this.comment(), newComment, fromNote);
  }

  /**
   * Cancels edition of current comment.
   * @public
   * @param {Event} [event] event from DOM
   * @returns {Promise<boolean>} `true` if the cancel was successful, `false` otherwise.
   */
  public async cancel(event?: Event): Promise<boolean> {
    // if event is present, it's by triggering dom event (click on cancel button).
    // We need to stop propagation to not trigger redirect in case of DM's comment
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.endEdit.emit();
    // We can cancel editing or creating comment.
    // on creation, title is empty or fromNote property is present (we delete fromNote property after transformation)
    const inEdition = this.comment().title && !this.comment().fromNote;
    if (inEdition) {
      return true;
    }
    return this.commentService
      .deleteComments([this.comment()])
      .then((res: boolean[]) => res.every(Boolean));
  }

  /**
   * Called when user leaves fields and when paragraphs changed.
   * If any changes are detected, adds current component to commentService.
   * If user restores data, removes current component from commentService.
   * @public
   * @returns {void}
   */
  public formChanged(): void {
    isEqual(
      {
        title: this.comment().title,
        code: this.comment().code,
        priority: this.comment().priority,
        model: this.comment().model,
        cageCode: this.comment().cageCode,
        year: this.comment().year,
        seqNumber: this.comment().seqNumber,
        commentType: this.comment().commentType,
        paragraphs: this.comment().contents,
        medias: this.comment().medias
      },
      this.form.getRawValue()
    )
      ? this.commentService.removeCommentEditComponent(this)
      : this.commentService.addCommentEditComponent(this);
  }

  /**
   * Called when the user changes the code fields.
   * SPEC: We only want to display the first error.
   * @public
   * @returns {void}
   */
  public formCodeChanged(): void {
    this.firstCodeError = this.getFirstErrorCode();
  }

  /**
   * Gets the first error on the code form group.
   * All fields have the required and pattern validators.
   * We want to display the required error in priority.
   * @public
   * @returns {CommentCodeError} An Object that contains all the information needed to display and translate the error.
   */
  private getFirstErrorCode(): CommentCodeError {
    const codeGroup = this.form.get("code") as FormGroup;
    const firstInvalidField = Object.keys(codeGroup.controls).find(
      (key: string) => codeGroup.get(key)?.invalid
    );
    if (!firstInvalidField) {
      return undefined;
    }
    if (codeGroup.get(firstInvalidField).errors["required"]) {
      return { key: "form.error.required", field: firstInvalidField };
    }
    switch (firstInvalidField) {
      case "model":
        return {
          key: "form.error.size",
          field: firstInvalidField,
          min: 2,
          max: 14,
          typeChar: "characters"
        };

      case "cageCode":
        return {
          key: "form.error.pattern",
          field: firstInvalidField,
          nbrChar: 5,
          typeChar: "characters"
        };
      case "year":
        return {
          key: "form.error.pattern",
          field: firstInvalidField,
          nbrChar: 4,
          typeChar: "numbers"
        };
      case "seqNumber":
        return {
          key: "form.error.pattern",
          field: firstInvalidField,
          nbrChar: 5,
          typeChar: "numbers"
        };
      default:
        return undefined;
    }
  }

  /**
   * Adds input into formParagraphs.
   */
  public addParagraph(): void {
    this.formParagraphs.push(this.formBuilder.control(""), { emitEvent: false });
    this.formChanged();
  }

  /**
   * Removes the input at the given index from formParagraphs.
   * @param paragraphIndex
   */
  public removeParagraph(paragraphIndex: number): void {
    // SPEC: The first paragraph is mandatory and musn't be deleted.
    if (paragraphIndex === 0) {
      return;
    }
    this.formParagraphs.removeAt(paragraphIndex, { emitEvent: false });
    this.formChanged();
  }

  /**
   * Code group validator that checks if the comment code is unique.
   * @returns {ValidationErrors} A function that sets the duplicate error to the code control if the code is duplicated.
   */
  private checkDuplicate: ValidatorFn = (group: AbstractControl): ValidationErrors => {
    const code = this.commentService.constructCommentCode(group.getRawValue());
    const isDuplicateCode = this.commentService.isDuplicateCode(this.comment()._id, code);
    return isDuplicateCode ? { duplicate: true } : null;
  };

  /**
   * On click to "fileInput" button
   * Add media to form
   * @param event
   */
  public async addMedia(event: Event): Promise<void> {
    if (!this.commentService.canAttachMedia) {
      return;
    }
    const file = (event.target as HTMLInputElement).files[0];
    (event.target as HTMLInputElement).value = "";
    try {
      const media = await this.commentService.attachMediaToComment(file, this.comment());
      this.formMedias.push(this.formBuilder.control(media), { emitEvent: false });
      this.formChanged();
    } catch (error) {
      this.messageService.error(error.message);
    }
  }

  /**
   *
   * Remove the media from the current comment
   * @param mediaIndex
   */
  public removeMedia(mediaIndex: number): void {
    if (!this.commentService.canAttachMedia) {
      return;
    }
    this.formMedias.removeAt(mediaIndex, { emitEvent: false });
    this.formChanged();
  }

  /**
   * Open the media in a new tab for the browser app.
   * Download the media for the cordova and electron apps.
   * @param event
   * @param mediaId
   * @param fileName
   */
  public openMedia(event: Event, mediaId: string, fileName: string): void {
    event.stopPropagation();
    event.preventDefault();
    if (!this.commentService.canAttachMedia) {
      return;
    }
    this.commentService.openMedia(mediaId, fileName);
  }
}
